import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccesAdmin } from '../acces';
import { catalogueCall } from './catalogueCall';
import './AdminCataloguePage.css';
import { Field, FieldGroup } from './Field.jsx';
import { AdminPromoCodes } from './AdminPromoCodes.jsx';
import { AdminAppliMobile } from './AdminAppliMobile.jsx';
import { AdminPacks } from './AdminPacks.jsx';

/**
 * Le catalogue commercial de la suite, au back-office du PORTAIL (27/09/2026).
 *
 * Une formule décrit ce qu'un espace achète dans TOUTE la suite ; les tables
 * et les routes vivent chez AuthSEmply depuis le déplacement des abonnements.
 * L'ÉCRAN, lui, était resté dans le back-office de SEmplyApp (« le portail
 * n'a pas de back-office »), et chaque produit en avait recopié une partie
 * (Formules et Codes chez Compta, Previ et Immo). Quatre écrans pour une seule
 * vérité : il n'en reste qu'un, ici, pour les quatre produits.
 *
 * Habilitation : superadmin de la suite, session ouverte avec mot de passe ET
 * second facteur — `AdminGuard` la relit à chaque requête, sur la session du
 * portail (cookie), sans jeton à transmettre.
 *
 * Repris de SEmplyApp (AdminPlansPage.jsx) avec ses règles : formules nées
 * fermées, prix Stripe rattachés et jamais créés ici, matrice des droits.
 */

const PRODUCTS = [
  { id: 'APP', label: 'SEmplyApp' },
  { id: 'PREVI', label: 'SEmplyPrevi' },
  { id: 'COMPTA', label: 'SEmplyCompta' },
  { id: 'IMMO', label: 'SEmplyImmo' },
];

// Gratuite ou payante (18/10/2026) — plus de niveaux Light/Medium/Full : ce
// qu'ouvre une formule est la grille cochée ci-dessous, rien d'autre. La
// gratuite n'est pas une formule comme les autres : c'est le PLANCHER de droits
// du produit — ce dont dispose un espace qui n'a rien souscrit. Une seule par
// produit ; ACTIVE, elle ouvre le produit à tout compte de la suite ;
// désactivée, elle n'existe pas. Elle n'a pas de prix Stripe, par construction.

/** Une formule gratuite n'a pas de prix : le champ Stripe n'a rien à demander. */
const estGratuite = (plan) => Boolean(plan?.gratuite);

const FEATURE_KINDS = [
  { id: 'BOOLEAN', label: 'Droit (oui / non)' },
  { id: 'QUOTA', label: 'Quota (plafond chiffré)' },
];

const EMPTY_PLAN = {
  id: null,
  name: '',
  product: 'APP',
  gratuite: false,
  includes: [],
  /** Offre groupée : formule choisie pour chaque produit inclus (id). */
  plansInclusIds: [],
  priceMonthly: 0,
  stripePriceId: '',
  stripeProductId: '',
  description: '',
  // Une formule naît FERMÉE, comme dans les back-offices de Compta et de Previ.
  // Elle partait en vente, et le formulaire enregistre le plan ET sa matrice
  // d'un seul geste : il suffisait d'enregistrer avant d'avoir coché quoi que
  // ce soit pour mettre en vente une offre qui n'ouvre rien. On coche « En
  // vente » une fois le contenu réglé — c'est un geste de plus, et c'est le
  // seul moment où l'on regarde vraiment ce qu'on s'apprête à vendre.
  active: false,
  position: 100,
};

const EMPTY_FEATURE = {
  id: null,
  key: '',
  label: '',
  description: '',
  product: 'APP',
  kind: 'BOOLEAN',
  category: '',
  position: 100,
  active: true,
};

const productLabel = (id) => PRODUCTS.find((p) => p.id === id)?.label ?? id;

const euros = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Back-office du catalogue commercial de la suite.
 *
 * Onglets :
 *  - Formules : ce qui est vendu (prix Stripe rattaché) et ce que chaque
 *    formule débloque (matrice de fonctionnalités + plafonds).
 *  - Fonctionnalités : le catalogue des clés consommées par la nav et les
 *    guards (`app.menu.ventes`…).
 *  - Codes et réductions.
 *  - Packs : ventes ponctuelles (crédits, capacités), 04/10/2026.
 *  - Appli mobile : le changement de formule depuis l'appli (01/10/2026).
 *
 * Accès contrôlé côté serveur (`AdminGuard` du portail) ; le front lit
 * `GET /admin/moi` (AdminLayout) uniquement pour l'affichage.
 */
function AdminCataloguePage() {
  const [allowed, setAllowed] = useState(null); // null = vérification en cours
  const [tab, setTab] = useState('plans');

  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [stripePrices, setStripePrices] = useState([]);
  const [stripeError, setStripeError] = useState('');

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  // { [featureId]: { checked: bool, limit: string } } — état local de la matrice.
  const [matrix, setMatrix] = useState({});

  const [featureForm, setFeatureForm] = useState(EMPTY_FEATURE);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Pas de mode « sudo » : le portail exige que la SESSION elle-même ait été
  // ouverte avec mot de passe et second facteur (`amr`), vérifié par
  // `AdminGuard` à chaque requête. On ne l'obtient pas en cours de route.
  const withSudo = (action) => action();
  const sudoPrompt = null;
  // Superadmin seul : un agent support n'administre pas le catalogue (le
  // serveur le refuse de toute façon).
  const acces = useAccesAdmin();
  const canWrite = acces.superadmin;
  const profileLabel = null;

  const call = catalogueCall;

  const loadPlans = useCallback(async () => {
    setPlans(await call('/admin/plans'));
  }, [call]);

  const loadFeatures = useCallback(async () => {
    setFeatures(await call('/admin/features'));
  }, [call]);

  const loadStripePrices = useCallback(async () => {
    try {
      setStripePrices(await call('/admin/stripe/prices'));
      setStripeError('');
    } catch (e) {
      // Clé Stripe absente en local : on retombe sur la saisie manuelle du priceId.
      setStripeError(String(e.message ?? e));
    }
  }, [call]);

  useEffect(() => {
    (async () => {
      try {
        if (!acces.superadmin) { setAllowed(false); return; }
        setAllowed(true);
        await Promise.all([loadPlans(), loadFeatures(), loadStripePrices()]);
      } catch (e) {
        // Session sans second facteur : le guard répond `admin_amr_required`.
        // On l'oriente vers la reconnexion plutôt que d'afficher un refus sec.
        if (e?.code === 'admin_amr_required') { setAllowed('mfa'); return; }
        setAllowed(false);
      }
    })();
  }, [acces.superadmin, loadPlans, loadFeatures, loadStripePrices]);

  // ─── Formules ─────────────────────────────────────────────────────────────

  const selectPlan = (plan) => {
    setError(''); setNotice('');
    setSelectedPlanId(plan.id);
    setPlanForm({
      id: plan.id,
      name: plan.name,
      product: plan.product,
      gratuite: plan.gratuite,
      includes: plan.includes ?? [],
      plansInclusIds: (plan.inclusions ?? []).map((i) => i.planInclus.id),
      priceMonthly: plan.priceMonthly,
      // `?? ''` : une formule gratuite n'a pas de prix Stripe, et le champ
      // contrôlé refuse null — c'est ce qui faisait planter l'écran dès
      // qu'on ouvrait la formule gratuite.
      stripePriceId: plan.stripePriceId ?? '',
      stripeProductId: plan.stripeProductId ?? '',
      description: plan.description ?? '',
      active: plan.active,
      position: plan.position,
    });
    setMatrix(
      Object.fromEntries(
        (plan.features ?? []).map((pf) => [
          pf.featureId,
          { checked: true, limit: pf.limit === null ? '' : String(pf.limit) },
        ]),
      ),
    );
  };

  const newPlan = () => {
    setError(''); setNotice('');
    setSelectedPlanId(null);
    setPlanForm(EMPTY_PLAN);
    setMatrix({});
  };

  const setPlanField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPlanForm((f) => ({ ...f, [key]: value }));
  };

  const toggleInclude = (product) => {
    setPlanForm((f) => ({
      ...f,
      includes: f.includes.includes(product)
        ? f.includes.filter((p) => p !== product)
        : [...f.includes, product],
    }));
  };

  /** Offre groupée : la formule d'un produit inclus (vide = sa formule gratuite). */
  const choisirPlanInclus = (produitInclus, planId) => {
    setPlanForm((f) => ({
      ...f,
      plansInclusIds: [
        ...f.plansInclusIds.filter((pid) => plans.find((x) => x.id === pid)?.product !== produitInclus),
        ...(planId ? [planId] : []),
      ],
    }));
  };

  /** Rattache un prix Stripe : récupère au passage le produit et le montant. */
  const pickStripePrice = (e) => {
    const priceId = e.target.value;
    const price = stripePrices.find((p) => p.id === priceId);
    setPlanForm((f) => ({
      ...f,
      stripePriceId: priceId,
      stripeProductId: price?.productId ?? f.stripeProductId,
      // Le montant Stripe fait foi : `priceMonthly` n'est qu'un affichage.
      priceMonthly: price?.amount != null ? price.amount : f.priceMonthly,
      name: f.name || price?.productName || '',
    }));
  };

  const savePlan = async () => {
    /**
     * Deux refus avant d'enregistrer, et ils ne valent que pour la MISE EN VENTE
     * d'une formule payante.
     *
     * Ils sont la contrepartie de la naissance fermée : sans eux, l'étape qu'on
     * vient d'ajouter ne servirait à rien, puisqu'on pourrait cocher « En vente »
     * du même geste. Ici la matrice est enregistrée AVEC le plan, si bien que
     * l'état coché à l'écran est ce qui fait foi — pas ce que le serveur détient
     * encore.
     *
     * La formule GRATUITE en est exemptée : ouvrir un produit au gratuit sans
     * cocher la moindre fonctionnalité reste un choix légitime — le plancher
     * accorde l'accès au produit lui-même, et rien de plus. C'est la même
     * frontière que dans les back-offices de Compta et de Previ.
     */
    const coches = Object.values(matrix).filter((v) => v.checked).length;
    const prixStripe = estGratuite(planForm)
      ? null
      : (planForm.stripePriceId ?? '').trim();

    if (planForm.active && !estGratuite(planForm)) {
      if (coches === 0) {
        setNotice('');
        setError(
          `« ${planForm.name || 'Cette formule'} » n'ouvre aucune fonctionnalité : cochez son contenu ` +
            `avant de la mettre en vente. Sinon le client paie un accès vide, et ne voit aucun écran.`,
        );
        return;
      }
      if (!prixStripe) {
        setNotice('');
        setError(
          `« ${planForm.name || 'Cette formule'} » n'a pas de prix Stripe : elle serait « en vente » en base ` +
            `et absente de la grille tarifaire, sans que rien ne l'explique. Rattachez un prix, ` +
            `ou décochez « En vente ».`,
        );
        return;
      }
    }

    setSaving(true); setError(''); setNotice('');
    const payload = {
      name: planForm.name.trim(),
      product: planForm.product,
      gratuite: planForm.gratuite,
      includes: planForm.includes,
      // Seulement les formules des produits encore cochés.
      plansInclusIds: planForm.plansInclusIds.filter((pid) =>
        planForm.includes.includes(plans.find((x) => x.id === pid)?.product),
      ),
      priceMonthly: Number(planForm.priceMonthly) || 0,
      // Une formule gratuite part avec `null` : la colonne est unique, et
      // plusieurs NULL y sont permis — c'est prévu pour ça.
      stripePriceId: prixStripe,
      stripeProductId: planForm.stripeProductId.trim() || null,
      description: planForm.description.trim() || null,
      active: planForm.active,
      position: Number(planForm.position) || 100,
    };
    try {
      // Une seule enveloppe pour les deux appels : la modale n'apparaît qu'une
      // fois, et l'enregistrement complet est rejoué après confirmation.
      const saved = await withSudo(async () => {
        const plan = planForm.id
          ? await call(`/admin/plans/${planForm.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
          : await call('/admin/plans', { method: 'POST', body: JSON.stringify(payload) });

        // La matrice se sauvegarde dans un second appel : le PATCH ne la touche pas.
        await call(`/admin/plans/${plan.id}/features`, {
          method: 'PUT',
          body: JSON.stringify({
            // Les fonctionnalités du SEUL produit vendu : celles d'un produit
            // inclus viennent de la formule incluse choisie (18/10/2026).
            features: Object.entries(matrix)
              .filter(([, v]) => v.checked)
              .filter(([featureId]) => features.find((f) => f.id === featureId)?.product === planForm.product)
              .map(([featureId, v]) => ({
                featureId,
                limit: v.limit === '' ? null : Number(v.limit),
              })),
          }),
        });
        return plan;
      });

      setNotice(
        planForm.id
          ? 'Formule mise à jour.'
          : payload.active
            ? 'Formule créée et mise en vente.'
            : 'Formule créée, et fermée. Cochez « En vente » quand son contenu vous convient.',
      );
      await loadPlans();
      await loadStripePrices();
      setSelectedPlanId(saved.id);
      setPlanForm((f) => ({ ...f, id: saved.id }));
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Supprimer la formule « ${plan.name} » ? Préférez la désactiver si elle a déjà été vendue.`)) return;
    setError(''); setNotice('');
    try {
      await withSudo(() => call(`/admin/plans/${plan.id}`, { method: 'DELETE' }));
      setNotice('Formule supprimée.');
      newPlan();
      await loadPlans();
    } catch (e) {
      setError(String(e.message ?? e));
    }
  };

  const toggleFeatureInMatrix = (featureId) => {
    setMatrix((m) => {
      const current = m[featureId];
      if (current?.checked) return { ...m, [featureId]: { ...current, checked: false } };
      return { ...m, [featureId]: { checked: true, limit: current?.limit ?? '' } };
    });
  };

  const setMatrixLimit = (featureId, value) => {
    setMatrix((m) => ({ ...m, [featureId]: { checked: true, limit: value } }));
  };

  // ─── Fonctionnalités ──────────────────────────────────────────────────────

  const editFeature = (f) => {
    setError(''); setNotice('');
    setFeatureForm({
      id: f.id,
      key: f.key,
      label: f.label,
      description: f.description ?? '',
      product: f.product,
      kind: f.kind,
      category: f.category ?? '',
      position: f.position,
      active: f.active,
    });
  };

  const setFeatureField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFeatureForm((f) => ({ ...f, [key]: value }));
  };

  const saveFeature = async () => {
    setSaving(true); setError(''); setNotice('');
    const base = {
      label: featureForm.label.trim(),
      description: featureForm.description.trim() || null,
      product: featureForm.product,
      kind: featureForm.kind,
      category: featureForm.category.trim() || null,
      position: Number(featureForm.position) || 100,
      active: featureForm.active,
    };
    try {
      await withSudo(() =>
        featureForm.id
          ? call(`/admin/features/${featureForm.id}`, { method: 'PATCH', body: JSON.stringify(base) })
          : call('/admin/features', {
              method: 'POST',
              body: JSON.stringify({ ...base, key: featureForm.key.trim().toLowerCase() }),
            }),
      );
      setNotice(featureForm.id ? 'Fonctionnalité mise à jour.' : 'Fonctionnalité créée.');
      setFeatureForm(EMPTY_FEATURE);
      await loadFeatures();
      await loadPlans();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const deleteFeature = async (f) => {
    if (!window.confirm(`Supprimer « ${f.label} » (${f.key}) ?`)) return;
    setError(''); setNotice('');
    try {
      await withSudo(() => call(`/admin/features/${f.id}`, { method: 'DELETE' }));
      setNotice('Fonctionnalité supprimée.');
      await loadFeatures();
    } catch (e) {
      setError(String(e.message ?? e));
    }
  };

  // ─── Rendu ────────────────────────────────────────────────────────────────

  // Fonctionnalités proposables pour la formule en cours : celles du produit
  // vendu SEULEMENT. Un produit inclus prend celles de la formule incluse
  // choisie au-dessus (18/10/2026) — une seule façon de le régler.
  const matrixFeatures = useMemo(() => {
    return features
      .filter((f) => f.product === planForm.product)
      .reduce((groups, f) => {
        const key = f.category ?? 'Autres';
        (groups[key] ??= []).push(f);
        return groups;
      }, {});
  }, [features, planForm.product]);

  const plansByProduct = useMemo(
    () =>
      plans.reduce((groups, p) => {
        (groups[p.product] ??= []).push(p);
        return groups;
      }, {}),
    [plans],
  );

  if (allowed === null) return <div className="ap-page"><p>Vérification des droits…</p></div>;
  if (allowed === 'mfa') {
    return (
      <div className="ap-page">
        <h1>Double authentification requise</h1>
        <p>
          Le catalogue exige une session ouverte avec votre mot de passe ET votre code à usage
          unique. Activez la double authentification depuis <a href="/compte">Mon compte</a> si
          ce n’est pas fait, puis déconnectez-vous et reconnectez-vous ainsi.
        </p>
      </div>
    );
  }
  if (allowed === false) {
    return (
      <div className="ap-page">
        <h1>Administration</h1>
        <p>Le catalogue est réservé aux superadmins de la suite.</p>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <h1>Formules &amp; fonctionnalités</h1>
      <p className="ap-muted">
        Les prix sont gérés dans Stripe : ici on rattache une formule à un prix existant. Les
        <strong> droits</strong> (menus accessibles, quotas) sont gérés en base et prennent effet à
        la prochaine émission de token du client (reconnexion ou rafraîchissement de session).
      </p>

      <div className="ap-tabs">
        <button className={tab === 'plans' ? 'active' : ''} onClick={() => setTab('plans')}>
          Formules ({plans.length})
        </button>
        <button className={tab === 'features' ? 'active' : ''} onClick={() => setTab('features')}>
          Fonctionnalités ({features.length})
        </button>
        <button className={tab === 'codes' ? 'active' : ''} onClick={() => setTab('codes')}>
          Codes et réductions
        </button>
        <button className={tab === 'packs' ? 'active' : ''} onClick={() => setTab('packs')}>
          Packs
        </button>
        <button className={tab === 'mobile' ? 'active' : ''} onClick={() => setTab('mobile')}>
          Appli mobile
        </button>
      </div>

      {!canWrite && (
        <div className="ap-readonly">
          Profil {profileLabel ?? 'de consultation'} : le catalogue est en lecture seule.
          Prix, droits et plafonds ne peuvent pas être modifiés.
        </div>
      )}

      {error && <div className="ap-error">{error}</div>}
      {notice && <div className="ap-notice">{notice}</div>}

      {tab === 'plans' && (
        <div className="ap-layout">
          {/* ————— Liste des formules ————— */}
          <aside className="ap-list">
            {canWrite && (
              <button className="ap-btn primary block" onClick={newPlan}>+ Nouvelle formule</button>
            )}
            {PRODUCTS.map((p) => (
              <section key={p.id}>
                <h3>{p.label}</h3>
                {(plansByProduct[p.id] ?? []).length === 0 && <p className="ap-muted small">Aucune formule.</p>}
                {(plansByProduct[p.id] ?? []).map((plan) => (
                  <button
                    key={plan.id}
                    className={`ap-plan-item${selectedPlanId === plan.id ? ' selected' : ''}`}
                    onClick={() => selectPlan(plan)}
                  >
                    <span className="ap-plan-name">
                      {plan.name}
                      {!plan.active && <em className="ap-badge muted">inactive</em>}
                    </span>
                    <span className="ap-plan-meta">
                      {plan.gratuite ? 'Gratuite' : 'Payante'} · {euros(plan.priceMonthly)}/mois · {plan.features.length} droits
                      {plan._count?.subscriptions > 0 && ` · ${plan._count.subscriptions} abonné(s)`}
                    </span>
                  </button>
                ))}
              </section>
            ))}
          </aside>

          {/* ————— Éditeur ————— */}
          <section className="ap-form">
            <h2>{planForm.id ? `Modifier « ${planForm.name} »` : 'Nouvelle formule'}</h2>

            <Field as={null} label="Nom commercial *">
                {(id) => (
                <input id={id} value={planForm.name} onChange={setPlanField('name')} placeholder="Ex. SEmplyApp Essentiel" />
                )}
            </Field>

            <div className="ap-row">
              <div>
                <Field as={null} label="Produit vendu">
                    {(id) => (
                    <select id={id} value={planForm.product} onChange={setPlanField('product')}>
                      {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    )}
                </Field>
              </div>
              <div>
                <label className="ap-check ap-check-gratuite">
                  <input type="checkbox" checked={planForm.gratuite} onChange={setPlanField('gratuite')} />
                  Formule gratuite (plancher de droits du produit)
                </label>
              </div>
              <div>
                <Field as={null} label="Position">
                    {(id) => (
                    <input id={id} type="number" value={planForm.position} onChange={setPlanField('position')} />
                    )}
                </Field>
              </div>
            </div>

            {/* Groupe de cases, pas un champ : chaque case porte déjà son
                propre <label> englobant. */}
            <FieldGroup label="Produits inclus (bundle)" className="ap-checks">
                  {PRODUCTS.filter((p) => p.id !== planForm.product).map((p) => (
                    <label key={p.id} className="ap-check">
                      <input
                        type="checkbox"
                        checked={planForm.includes.includes(p.id)}
                        onChange={() => toggleInclude(p.id)}
                      />
                      {p.label}
                    </label>
                  ))}
            </FieldGroup>
            <p className="ap-hint">
              Un produit inclus donne accès à l’application correspondante sans souscription
              séparée, avec les fonctionnalités de la formule choisie ci-dessous (sans choix : celles
              de sa formule gratuite).
            </p>
            {planForm.includes.map((produitInclus) => {
              const candidates = plans.filter((x) => x.product === produitInclus && !x.gratuite);
              const choisie =
                planForm.plansInclusIds.find((pid) => candidates.some((c) => c.id === pid)) ?? '';
              const libelle = PRODUCTS.find((x) => x.id === produitInclus)?.label ?? produitInclus;
              return (
                <Field key={produitInclus} as={null} label={`Formule ${libelle} incluse`}>
                  {(id) => (
                    <select id={id} value={choisie} onChange={(e) => choisirPlanInclus(produitInclus, e.target.value)}>
                      <option value="">— Formule gratuite de {libelle} —</option>
                      {candidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.active ? '' : ' (inactive)'}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              );
            })}

            {/* Le contrôle change selon que la liste Stripe est joignable ou
                non : l'id doit donc être posé dans LES DEUX branches. */}
            {estGratuite(planForm) ? (
              <p className="ap-hint">
                Une formule gratuite n’a pas de prix Stripe : elle n’est pas vendue, elle
                définit ce dont dispose un espace sans abonnement. Décochez « Active » pour
                fermer le gratuit de ce produit — l’accès tombe alors au prochain jeton émis.
              </p>
            ) : (
            <Field as={null} label="Prix Stripe *">
              {(id) => stripePrices.length > 0 ? (
              <select id={id} value={planForm.stripePriceId} onChange={pickStripePrice}>
                <option value="">— Choisir un prix Stripe —</option>
                {stripePrices.map((p) => (
                  <option
                    key={p.id}
                    value={p.id}
                    disabled={p.linked && p.id !== planForm.stripePriceId}
                  >
                    {p.productName ?? p.nickname ?? p.id}
                    {p.amount != null && ` — ${euros(p.amount)}`}
                    {p.interval && ` / ${p.interval}`}
                    {p.linked && p.id !== planForm.stripePriceId ? ' (déjà rattaché)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input
                  id={id}
                  value={planForm.stripePriceId}
                  onChange={setPlanField('stripePriceId')}
                  placeholder="price_..."
                />
                <p className="ap-hint">
                  Liste Stripe indisponible ({stripeError || 'clé absente'}) : saisissez l’identifiant
                  de prix manuellement.
                </p>
              </>
              )}
            </Field>
            )}

            <div className="ap-row">
              <div>
                <Field as={null} label="Prix mensuel affiché (€)">
                    {(id) => (
                    <input id={id} type="number" step="0.01" value={planForm.priceMonthly} onChange={setPlanField('priceMonthly')} />
                    )}
                </Field>
              </div>
              <div className="ap-inline-check">
                {/* Le même interrupteur, deux sens : « en vente » pour une
                    formule payante, « produit ouvert au gratuit » pour la
                    formule FREE — qui n'est pas vendue mais dont l'activation
                    donne accès au produit à tout compte de la suite. Le dire
                    évite de fermer un gratuit en croyant retirer une offre du
                    catalogue. */}
                <label className="ap-check">
                  <input type="checkbox" checked={planForm.active} onChange={setPlanField('active')} />
                  {estGratuite(planForm) ? 'Version gratuite ouverte' : 'En vente'}
                </label>
              </div>
            </div>

            <Field as={null} label="Description (optionnel)">
                {(id) => (
                <textarea id={id} rows={2} value={planForm.description} onChange={setPlanField('description')} />
                )}
            </Field>

            {/* ————— Matrice des droits ————— */}
            <h3 className="ap-section">Ce que la formule débloque</h3>
            {Object.keys(matrixFeatures).length === 0 && (
              <p className="ap-muted">
                Aucune fonctionnalité déclarée pour {productLabel(planForm.product)} — créez-en dans
                l’onglet « Fonctionnalités ».
              </p>
            )}
            {Object.entries(matrixFeatures).map(([category, list]) => (
              <div key={category} className="ap-feature-group">
                <h4>{category}</h4>
                {list.map((f) => {
                  const state = matrix[f.id];
                  const checked = Boolean(state?.checked);
                  return (
                    <div key={f.id} className={`ap-feature-row${f.active ? '' : ' disabled'}`}>
                      <label className="ap-check">
                        <input type="checkbox" checked={checked} onChange={() => toggleFeatureInMatrix(f.id)} />
                        <span>
                          {f.label}
                          <code>{f.key}</code>
                          {!f.active && <em className="ap-badge muted">désactivée</em>}
                        </span>
                      </label>
                      {f.kind === 'QUOTA' && checked && (
                        <span className="ap-limit">
                          <input
                            type="number"
                            min="0"
                            placeholder="illimité"
                            value={state?.limit ?? ''}
                            onChange={(e) => setMatrixLimit(f.id, e.target.value)}
                          />
                          <small>vide = illimité</small>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {canWrite && (
              <div className="ap-actions">
                <button className="ap-btn primary" onClick={savePlan} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                {planForm.id && (
                  // Formule gratuite enregistrée : plancher de droits, que l'API
                  // refuse de supprimer — bouton grisé, avec la raison.
                  <button
                    className="ap-btn danger"
                    onClick={() => deletePlan(plans.find((p) => p.id === planForm.id))}
                    disabled={saving || estGratuite(plans.find((p) => p.id === planForm.id))}
                    title={
                      estGratuite(plans.find((p) => p.id === planForm.id))
                        ? 'La formule gratuite est le plancher de droits du produit : elle ne se supprime pas. Décochez « Version gratuite ouverte » pour la fermer.'
                        : undefined
                    }
                  >
                    Supprimer
                  </button>
                )}
                <button className="ap-btn" onClick={newPlan} disabled={saving}>Annuler</button>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === 'features' && (
        <div className="ap-layout">
          {/* ————— Catalogue ————— */}
          <aside className="ap-list wide">
            {canWrite && (
              <button className="ap-btn primary block" onClick={() => setFeatureForm(EMPTY_FEATURE)}>
                + Nouvelle fonctionnalité
              </button>
            )}
            {PRODUCTS.map((p) => {
              const list = features.filter((f) => f.product === p.id);
              if (list.length === 0) return null;
              return (
                <section key={p.id}>
                  <h3>{p.label}</h3>
                  {list.map((f) => (
                    <div key={f.id} className="ap-feature-item">
                      <button className="ap-feature-edit" onClick={() => editFeature(f)}>
                        <span className="ap-plan-name">
                          {f.label}
                          {!f.active && <em className="ap-badge muted">inactive</em>}
                          {f.kind === 'QUOTA' && <em className="ap-badge">quota</em>}
                        </span>
                        <span className="ap-plan-meta">
                          <code>{f.key}</code> · {f._count?.plans ?? 0} formule(s)
                        </span>
                      </button>
                      {canWrite && (
                        <button className="ap-icon-btn" title="Supprimer" onClick={() => deleteFeature(f)}>✕</button>
                      )}
                    </div>
                  ))}
                </section>
              );
            })}
          </aside>

          {/* ————— Éditeur ————— */}
          <section className="ap-form">
            <h2>{featureForm.id ? `Modifier « ${featureForm.label} »` : 'Nouvelle fonctionnalité'}</h2>

            <Field as={null} label="Clé technique *">
                {(id) => (
                <input id={id}
                  value={featureForm.key}
                  onChange={setFeatureField('key')}
                  disabled={Boolean(featureForm.id)}
                  placeholder="app.menu.ventes"
                />
                )}
            </Field>
            <p className="ap-hint">
              {featureForm.id
                ? 'La clé est immuable : elle est référencée dans le code (nav, guards).'
                : 'Format « produit.famille.nom ». C’est cette clé que le code interroge.'}
            </p>

            <Field as={null} label="Libellé *">
                {(id) => (
                <input id={id} value={featureForm.label} onChange={setFeatureField('label')} placeholder="Ventes" />
                )}
            </Field>

            <div className="ap-row">
              <div>
                <Field as={null} label="Produit">
                    {(id) => (
                    <select id={id} value={featureForm.product} onChange={setFeatureField('product')}>
                      {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    )}
                </Field>
              </div>
              <div>
                <Field as={null} label="Type">
                    {(id) => (
                    <select id={id} value={featureForm.kind} onChange={setFeatureField('kind')}>
                      {FEATURE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                    </select>
                    )}
                </Field>
              </div>
              <div>
                <Field as={null} label="Position">
                    {(id) => (
                    <input id={id} type="number" value={featureForm.position} onChange={setFeatureField('position')} />
                    )}
                </Field>
              </div>
            </div>

            <Field as={null} label="Catégorie">
                {(id) => (
                <input id={id} value={featureForm.category} onChange={setFeatureField('category')} placeholder="Menus, Options, Quotas…" />
                )}
            </Field>

            <Field as={null} label="Description (optionnel)">
                {(id) => (
                <textarea id={id} rows={2} value={featureForm.description} onChange={setFeatureField('description')} />
                )}
            </Field>

            <label className="ap-check">
              <input type="checkbox" checked={featureForm.active} onChange={setFeatureField('active')} />
              Active
            </label>
            <p className="ap-hint">
              Désactiver retire le droit de tous les clients sans défaire les rattachements aux
              formules.
            </p>

            {canWrite && (
              <div className="ap-actions">
                <button className="ap-btn primary" onClick={saveFeature} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button className="ap-btn" onClick={() => setFeatureForm(EMPTY_FEATURE)} disabled={saving}>
                  Annuler
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Les codes ont leur propre fichier : ils ne partagent avec les formules
          que l'appel au portail et la liste des plans, et les mêler à un écran
          qui fait déjà deux choses en aurait fait un qui en fait trois. */}
      {tab === 'codes' && (
        <AdminPromoCodes call={call} plans={plans} products={PRODUCTS} canWrite={canWrite} />
      )}

      {/* Changement de formule depuis l'appli mobile (01/10/2026) : réglage de
          la plateforme (`/admin/ouverture`), présenté ici, avec les formules. */}
      {tab === 'mobile' && <AdminAppliMobile call={call} canWrite={canWrite} />}

      {/* Packs (04/10/2026) : ventes ponctuelles sur un quota du catalogue. */}
      {tab === 'packs' && (
        <AdminPacks
          call={call}
          products={PRODUCTS}
          features={features}
          stripePrices={stripePrices}
          canWrite={canWrite}
        />
      )}

      {/* Ré-authentification, affichée uniquement quand l'API la réclame. */}
      {sudoPrompt}
    </div>
  );
}

export { AdminCataloguePage };
export default AdminCataloguePage;
